import { KeyboardEvent, ReactNode } from "react";
import EvStationOutlined from "@mui/icons-material/EvStationOutlined";
import LocalParking from "@mui/icons-material/LocalParking";
import Check from "@mui/icons-material/Check";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

// The pieces of the booking app's form that the create and edit sheets share.

export type ParkingSpace = "Standard" | "EV";

// The booking app's parking type card: EV on green, Standard on the muted surface; the chosen one is outlined.
// Without onSelect it only shows the booking's type.
export const ParkingCard = ({ space, selected, onSelect, detail }: { space: ParkingSpace; selected: boolean; onSelect?: () => void; detail?: string }) => {
    const ev = space === "EV";
    const Icon = ev ? EvStationOutlined : LocalParking;
    const Card = onSelect ? "button" : "div";
    return (
        <Card {...(onSelect ? { type: "button" as const, role: "radio", "aria-checked": selected, onClick: onSelect } : {})}
            className={`relative flex-1 rounded-2xl p-4 text-left transition-colors ${ev ? "bg-ev-container" : "bg-app-muted"} ${selected ? "border-2 border-ev" : "border border-app-input"}`}>
            <Icon aria-hidden sx={{ fontSize: 28 }} className={ev ? "text-ev" : "text-app-text"} />
            <span className={`absolute right-4 top-4 flex h-[22px] w-[22px] items-center justify-center rounded-full ${selected ? "bg-ev text-white" : "border-2 border-app-input"}`}>
                {selected && <Check aria-hidden sx={{ fontSize: 16 }} />}
            </span>
            <p className="mt-2 text-base font-semibold text-app-text">{space}</p>
            <p className="text-xs text-app-secondary">{detail ?? (ev ? "EV charging" : "Standard parking")}</p>
        </Card>
    );
};

// Arrow keys move the choice within a radio group; only the chosen option is in the tab order.
export const moveWithArrows = <T extends string,>(event: KeyboardEvent<HTMLDivElement>, values: T[], value: T, onChange: (value: T) => void) => {
    const step = ["ArrowRight", "ArrowDown"].includes(event.key) ? 1 : ["ArrowLeft", "ArrowUp"].includes(event.key) ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const next = (values.indexOf(value) + step + values.length) % values.length;
    onChange(values[next]);
    (event.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]')[next])?.focus();
};

// The booking app's segmented control.
export const Segmented = <T extends string,>({ label, options, value, onChange }: {
    label: string; options: { value: T; label: string }[]; value: T; onChange: (value: T) => void;
}) => (
    <div>
        <p className="mb-2 text-sm font-semibold text-app-text">{label}</p>
        <div role="radiogroup" aria-label={label} className="flex rounded-xl bg-app-muted p-1"
            onKeyDown={(event) => moveWithArrows(event, options.map((option) => option.value), value, onChange)}>
            {options.map((option) => (
                <button key={option.value} type="button" role="radio" aria-checked={value === option.value} tabIndex={value === option.value ? 0 : -1}
                    onClick={() => onChange(option.value)}
                    className={`h-10 flex-1 rounded-lg text-sm font-semibold ${value === option.value ? "bg-white text-primary shadow-sm" : "text-app-text"}`}>
                    {option.label}
                </button>
            ))}
        </div>
    </div>
);

export const Notice = ({ tone = "info", children }: { tone?: "info" | "success"; children: ReactNode }) => (
    <div role="status" className={`flex items-start gap-2 rounded-xl p-3 text-[13px] ${tone === "success" ? "bg-ev-container text-ev" : "bg-app-muted text-primary"}`}>
        {tone === "success" ? <Check aria-hidden sx={{ fontSize: 18 }} /> : <InfoOutlined aria-hidden sx={{ fontSize: 18 }} />}
        <span>{children}</span>
    </div>
);
